import React from 'react';
import { ErrorMessage, Field, FieldArray, Form, Formik, useField } from 'formik';
import { wpSourceDefaults, typesenseSourceDefaults, iprSourceDefaults } from './commands';
import { IPR_CATEGORIES } from './iprCategories';
import Select from 'react-select';
import * as Yup from 'yup';
import { array, object, string, number } from "yup";

const iprOptions = IPR_CATEGORIES.map((cat) => ({value: cat.key, label: cat.name}));

const IprCategorySelect = ({name}) => {
  const [field, , helpers] = useField(name);
  const selectedValues = Array.isArray(field.value)
    ? field.value.map(v => iprOptions.find(opt => opt.value === v)).filter(Boolean)
    : [];

  const handleChange = (selected) => {
    helpers.setValue(selected ? selected.map(opt => opt.value) : []);
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? '#fff' : '#212529',
      backgroundColor: state.isSelected
        ? '#0d6efd'
        : state.isFocused
          ? '#e9ecef'
          : '#fff',
      fontWeight: state.isSelected ? '500' : '400',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#0d6efd',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#fff',
      fontWeight: '500',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#fff',
      ':hover': {
        backgroundColor: '#0b5ed7',
        color: '#fff',
      },
    }),
    control: (provided) => ({
      ...provided,
      borderColor: '#dee2e6',
      minHeight: '38px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d',
    }),
  };

  return (
    <Select
      isMulti
      name={name}
      options={iprOptions}
      value={selectedValues}
      onChange={handleChange}
      placeholder='Select categories...'
      classNamePrefix='react-select'
      isClearable
      styles={customStyles}
    />
  );
};

Yup.addMethod(Yup.string, 'commaSeparatedValues', function (errorMessage) {
  return this.test(`test-card-length`, errorMessage, function (value) {
    const { path, createError } = this;

    if (!value) {
      return true;
    }

    let processed = value
      .split(',')
      .map(item => item.trim())
      .filter(isFinite)
      .join(',');

    return (
      (value === processed) ||
      createError({ path, message: errorMessage })
    );
  });
});

const sourcesSchema = Yup.object().shape({
  wp: array()
    .of(
      object().shape({
        host: string().required("Can't be blank").url('Invalid URL'),
        categoryIds: string().commaSeparatedValues('Invalid format'),
        tagIds: string().commaSeparatedValues('Invalid format'),
        count: number().required("Can't be blank").positive('Must be a positive integer').integer(),
      })
    ),
  typesense: array()
    .of(
      object().shape({
        collection: string().required("Can't be blank"),
        filter_by: string().required("Can't be blank"),
        count: number().required("Can't be blank").positive('Must be a positive integer').integer(),
      })
    ),
  ipr: array()
    .of(
      object().shape({
        categories: array().of(string().required()).min(1, 'Select at least one category'),
        count: number().required("Can't be blank").positive('Must be a positive integer').integer(),
      })
    )
});

/**
 * Feed aggregator sources form.
 *
 * @param {Function} submitHandler
 * @param {Object} defaultValues
 * @returns {Element}
 * @constructor
 */
const FeedAggregatorSourcesForm = ({submitHandler, defaultValues}) => {
  return <>
    <Formik initialValues={defaultValues} validationSchema={sourcesSchema} onSubmit={values => submitHandler(values)}>
      {({values}) => (
        <Form>
          <div className='form-fields'>
          <h2 className='h--smallest text-light'>Typesense</h2>
            <FieldArray name='typesense'>
              {({remove, push}) => (
                <div className='col-12 mb-4'>
                  {values.typesense && values.typesense.length > 0 &&
                    values.typesense.map((entry, idx) => (
                      <div className='row mb-4' key={idx}>
                        <div className='col-12 mb-0'>
                          <label className='form-label' htmlFor={`typesense[${idx}].filter_by`}>Filter by</label>
                          <Field className='form-control form-control-sm' name={`typesense[${idx}].filter_by`} placeholder='content_types:=[`Documentation`] && products:=[`Riva`] && published_year:>=2022' type='text'/>
                          <ErrorMessage name={`typesense[${idx}].filter_by`} component='p' className='field-error mt-2 mb-0'/>
                          <p className='p--small text-light pt-1'>"Filter by" query string</p>
                        </div>
                        <div className='col-4 mb-3'>
                          <label className='form-label' htmlFor={`typesense[${idx}].collection`}>Collection name</label>
                          <Field className='form-control form-control-sm' name={`typesense[${idx}].collection`} placeholder='techblog_posts' type='text' required/>
                          <ErrorMessage name={`typesense[${idx}].collection`} component='p' className='field-error mt-2 mb-0'/>
                        </div>
                        <div className='col-4 mb-0'>
                          <label className='form-label' htmlFor={`typesense[${idx}].count`}>Count</label>
                          <Field className='form-control form-control-sm' name={`typesense[${idx}].count`} placeholder='3' type='number'/>
                          <ErrorMessage name={`typesense[${idx}].count`} component='p' className='field-error mt-2 mb-0'/>
                          <p className='p--small text-light pt-1'>Minimum # of items to show from this source</p>
                        </div>
                        <Field name={`typesense[${idx}].type`} type='hidden'/>
                        <div className='col-12 mb-3'>
                          <button type='button' className='btn btn-outline-warning btn-sm' onClick={() => remove(idx)}>Remove source</button>
                        </div>
                      </div>
                    ))}
                  <button type='button' className='btn btn-outline-light btn-sm' onClick={() => push({...typesenseSourceDefaults})}>
                    Add source
                  </button>
                </div>
              )}
            </FieldArray>

          <h2 className='h--smallest text-light'>NVIDIA News (iPR)</h2>
            <FieldArray name='ipr'>
              {({remove, push}) => (
                <div className='col-12 mb-4'>
                  {values.ipr && values.ipr.length > 0 &&
                    values.ipr.map((entry, idx) => (
                      <div className='row mb-4' key={idx}>
                        <div className='col-12 mb-3'>
                          <label className='form-label'>Categories</label>
                          <IprCategorySelect name={`ipr[${idx}].categories`}/>
                          <ErrorMessage name={`ipr[${idx}].categories`} component='p' className='field-error mt-2 mb-0'/>
                          <p className='p--small text-light pt-1'>NVIDIA News (iPR) RSS feed categories</p>
                        </div>
                        <div className='col-4 mb-0'>
                          <label className='form-label' htmlFor={`ipr[${idx}].count`}>Count</label>
                          <Field className='form-control form-control-sm' name={`ipr[${idx}].count`} placeholder='3' type='number'/>
                          <ErrorMessage name={`ipr[${idx}].count`} component='p' className='field-error mt-2 mb-0'/>
                          <p className='p--small text-light pt-1'>Minimum # of items to show from this source</p>
                        </div>
                        <Field name={`ipr[${idx}].type`} type='hidden'/>
                        <div className='col-12 mb-3'>
                          <button type='button' className='btn btn-outline-warning btn-sm' onClick={() => remove(idx)}>Remove source</button>
                        </div>
                      </div>
                    ))}
                  {(!values.ipr || values.ipr.length === 0) &&
                    <button type='button' className='btn btn-outline-light btn-sm' onClick={() => push({...iprSourceDefaults})}>
                      Add source
                    </button>
                  }
                </div>
              )}
            </FieldArray>

          <h2 className='h--smallest text-light'>WordPress API</h2>
          <FieldArray name='wp'>
            {({ remove, push }) => (
              <div className='col-12 mb-4'>
                {values.wp && values.wp.length > 0 &&
                  values.wp.map((entry, idx) => (
                    <div className='row mb-4' key={idx}>
                      <div className='col-12 mb-3'>
                        <label className='form-label' htmlFor={`wp[${idx}].host`}>Host</label>
                        <Field className='form-control form-control-sm' name={`wp[${idx}].host`} placeholder='https://developer.nvidia.com/blog' type='text' required/>
                        <ErrorMessage name={`wp[${idx}].host`} component='p' className='field-error mt-2 mb-0'/>
                        <p className='p--small text-light pt-1'>Base URL without a trailing slash (e.g., https://developer.nvidia.com/blog)</p>
                      </div>
                      <div className='col-4 mb-0'>
                        <label className='form-label' htmlFor={`wp[${idx}].count`}>Count</label>
                        <Field className='form-control form-control-sm' name={`wp[${idx}].count`} placeholder='3' type='number'/>
                        <ErrorMessage name={`wp[${idx}].count`} component='p' className='field-error mt-2 mb-0'/>
                        <p className='p--small text-light pt-1'>Minimum # of items to show from this source</p>
                      </div>
                      <div className='col-4 mb-0'>
                        <label className='form-label' htmlFor={`wp[${idx}].categoryIds`}>Categories</label>
                        <Field className='form-control form-control-sm' name={`wp[${idx}].categoryIds`} placeholder='123,456' type='text'/>
                        <ErrorMessage name={`wp[${idx}].categoryIds`} component='p' className='field-error mt-2 mb-0'/>
                        <p className='p--small text-light pt-1'>Comma separated list of categories IDs</p>
                      </div>
                      <div className='col-4 mb-0'>
                        <label className='form-label' htmlFor={`wp[${idx}].tagIds`}>Tags</label>
                        <Field className='form-control form-control-sm' name={`wp[${idx}].tagIds`} placeholder='123,456' type='text'/>
                        <ErrorMessage name={`wp[${idx}].tagIds`} component='p' className='field-error mt-2 mb-0'/>
                        <p className='p--small text-light pt-1'>Comma separated list of tags IDs</p>
                      </div>
                      <Field name={`wp[${idx}].type`} type='hidden'/>
                      <div className='col-12 mb-3'>
                        <button type='button' className='btn btn-outline-warning btn-sm' onClick={() => remove(idx)}>Remove source</button>
                      </div>
                    </div>
                  ))}
                <button type='button' className='btn btn-outline-light btn-sm' onClick={() => push({...wpSourceDefaults})}>
                  Add source
                </button>
              </div>
            )}
          </FieldArray>

          </div>
          <button type='submit' className='btn btn-info btn-sm'>Update sources</button>
        </Form>
      )}
    </Formik>
  </>;
};

export default FeedAggregatorSourcesForm;
